import { useSyncExternalStore } from 'react';
import { getKsdmConfig } from 'helpers/index';

/**
 * ═════════════════════════════════════════════════════════════════════════════
 * The manufacturer LOGO + INITIALS that the shared header paints — as a store
 * the header can subscribe to, instead of a value it reads once at first paint.
 *
 * ★ WHY THIS FILE EXISTS (Phase 7 · T2, `manufacturer`).
 *
 * The CMS Admin Manufacturer screen writes into the SHARED HEADER, twice, and
 * both writes are measured on the live `:8130` page (2026-08-05):
 *
 *  1. `view/mfg.js:150-156 updateLogo` and `:140-148 removeImg`
 *         $('#logo-img img').attr('src', <new logo | /static/images/kinetix_logo.png>)
 *     `#logo-img` is the header's logo anchor. Measured before/after a real
 *     upload: header `<img>` src went `/media/mfg/logo/kinetix_logo_fxudsOY.png`
 *     → `/media/mfg/logo/tiny_test.png`, and after Remove →
 *     `/static/images/kinetix_logo.png`.
 *  2. `view/mfg.js:180-182 change_input`
 *         if (name == 'initials') $('#manufacturer-initials').html(value)
 *     `#manufacturer-initials` is the header's user-area span. Measured: typing
 *     `KSX` in Initials and blurring changed the header span's innerHTML from
 *     `"\n KSL\n :\n "` to `"KSX"`.
 *
 * In React the header is `pages/dashboard/components/GlobalHeader.tsx`, a live
 * component on EVERY React route, and it read those two values straight out of
 * the server-injected `window.__KSDM_CONFIG__.mfg`. A CMS screen reaching in
 * with `document.querySelector('#logo-img img')` would be writing into another
 * component's DOM — the one thing the T2 brief forbade — and React would
 * silently clobber it on the header's next render.
 *
 * So the header's OWN source of truth is made writable instead. This module
 * owns `__KSDM_CONFIG__.mfg.{logo,initials}`: it keeps the config object in
 * sync (so anything else still reading it directly is not left stale) and
 * publishes changes through `useSyncExternalStore`. `GlobalHeader` swaps two
 * config reads for `useMfgProfile()`; nothing else in the header changes, and
 * on every route but `/admin/` the value is simply the boot value, forever.
 *
 * ★ REPORTED AS A SHELL CHANGE, not worked around in the module — the T2 brief
 * asked for exactly that call. It is the smallest seam that keeps the write
 * one-directional (screen → store → header) and leaves the header the only
 * thing that touches header DOM.
 *
 * ★ ONE DELIBERATE NON-REPRODUCTION, flagged for the orchestrator.
 * Legacy's `.html(value)` REPLACES the span's whole content, so the trailing
 * `:` separator is DESTROYED until the next page load: measured innerHTML
 * `"KSX"`, where the initial render is `"\n KSL\n :\n "`. React's header
 * renders `{mfgInitials} :`, so after an edit it shows `KSX :` — i.e. the
 * colon SURVIVES. Reproducing the legacy loss would mean teaching the shared
 * header (used by dashboard, project, AHU, reports, sizer, partners and user
 * settings) that its separator disappears after one CMS edit, to preserve what
 * is plainly a bug. Recorded here as a deviation with reason, per the
 * parity-only rule.
 * ═════════════════════════════════════════════════════════════════════════════
 */

export interface MfgProfile {
  /** `null` renders the static Kinetix fallback in the header. */
  logo: string | null;
  initials: string;
}

type Listener = () => void;
const listeners = new Set<Listener>();

let snapshot: MfgProfile = {
  logo: getKsdmConfig()?.mfg?.logo ?? null,
  initials: getKsdmConfig()?.mfg?.initials ?? '',
};

/**
 * Update the logo and/or initials the header paints. Writes through to
 * `__KSDM_CONFIG__.mfg` as well, so a later direct read of the boot config
 * agrees with what is on screen. A no-op when nothing actually changed, which
 * keeps `useSyncExternalStore`'s snapshot referentially stable.
 */
export const setMfgProfile = (patch: Partial<MfgProfile>): void => {
  const next: MfgProfile = { ...snapshot, ...patch };
  if (next.logo === snapshot.logo && next.initials === snapshot.initials) return;
  snapshot = next;
  const mfg = getKsdmConfig()?.mfg;
  if (mfg) {
    mfg.logo = next.logo;
    mfg.initials = next.initials;
  }
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = (): MfgProfile => snapshot;

/** The header's (and anyone else's) live view of the mfg logo + initials. */
export const useMfgProfile = (): MfgProfile =>
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
